import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Avatar,
  CircularProgress,
  Alert,
  Pagination,
  Stack,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import {
  useSubscriptions,
  useUpdateSubscriptionStatus,
} from "../../hooks/useSubscriptions";
import { format } from "date-fns";

const SubscriptionList = () => {
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusReason, setStatusReason] = useState("");

  // Fetch subscriptions data
  const { data, isLoading, error, refetch } = useSubscriptions({
    page,
    limit: rowsPerPage,
    status: statusFilter === "all" ? undefined : statusFilter,
    search: searchTerm || undefined,
    sortBy,
    sortOrder,
  });

  const updateStatusMutation = useUpdateSubscriptionStatus();

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const handleStatusUpdate = (subscription) => {
    setSelectedSubscription(subscription);
    setNewStatus(subscription.isActive ? "cancelled" : "active");
    setStatusDialogOpen(true);
  };

  const handleStatusDialogClose = () => {
    setStatusDialogOpen(false);
    setSelectedSubscription(null);
    setNewStatus("");
    setStatusReason("");
  };

  const handleStatusSubmit = async () => {
    if (!selectedSubscription) return;

    await updateStatusMutation.mutateAsync({
      id: selectedSubscription._id,
      status: newStatus,
      reason: statusReason,
    });

    handleStatusDialogClose();
  };

  const handleRefresh = () => {
    refetch();
  };

  const getStatusChip = (isActive) => {
    return (
      <Chip
        label={isActive ? "Active" : "Inactive"}
        color={isActive ? "success" : "error"}
        size="small"
        variant="outlined"
      />
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "MMM dd, yyyy");
    } catch {
      return "Invalid Date";
    }
  };

  const formatAmount = (amount) => {
    if (!amount) return "N/A";
    return typeof amount === "string" ? amount : `$${amount}`;
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load subscriptions. Please try again.
        </Alert>
        <Button variant="contained" onClick={refetch}>
          Retry
        </Button>
      </Box>
    );
  }

  const subscriptions = data?.data || [];
  const pagination = data?.pagination || {};

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Subscriptions
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            /* Handle create new subscription */
          }}
        >
          New Subscription
        </Button>
      </Box>

      {/* Filters and Search */}
      <Card
        sx={{
          mb: 3,
          bgcolor: theme.palette.background.default,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Search subscriptions..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Rows per page</InputLabel>
                <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                fullWidth
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {subscriptions.length} of {pagination.total || 0}{" "}
          subscriptions
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Page {pagination.page || 1} of {pagination.pages || 1}
        </Typography>
      </Box>

      {/* Subscriptions Table */}
      <TableContainer
        component={Box}
        variant="outlined"
        sx={{
          bgcolor: theme.palette.background.default,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius:'3px'
        }}
      >
        <Table>
          <TableHead
            sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
          >
            <TableRow>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => handleSortChange("planName")}
                  endIcon={
                    sortBy === "planName"
                      ? sortOrder === "asc"
                        ? "↑"
                        : "↓"
                      : null
                  }
                  sx={{ textTransform: "none", fontWeight: "bold" }}
                >
                  Plan
                </Button>
              </TableCell>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => handleSortChange("userId")}
                  endIcon={
                    sortBy === "userId"
                      ? sortOrder === "asc"
                        ? "↑"
                        : "↓"
                      : null
                  }
                  sx={{ textTransform: "none", fontWeight: "bold" }}
                >
                  User
                </Button>
              </TableCell>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => handleSortChange("amount")}
                  endIcon={
                    sortBy === "amount"
                      ? sortOrder === "asc"
                        ? "↑"
                        : "↓"
                      : null
                  }
                  sx={{ textTransform: "none", fontWeight: "bold" }}
                >
                  Amount
                </Button>
              </TableCell>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => handleSortChange("interval")}
                  endIcon={
                    sortBy === "interval"
                      ? sortOrder === "asc"
                        ? "↑"
                        : "↓"
                      : null
                  }
                  sx={{ textTransform: "none", fontWeight: "bold" }}
                >
                  Interval
                </Button>
              </TableCell>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => handleSortChange("startDate")}
                  endIcon={
                    sortBy === "startDate"
                      ? sortOrder === "asc"
                        ? "↑"
                        : "↓"
                      : null
                  }
                  sx={{ textTransform: "none", fontWeight: "bold" }}
                >
                  Start Date
                </Button>
              </TableCell>
              <TableCell>
                <Button
                  size="small"
                  onClick={() => handleSortChange("isActive")}
                  endIcon={
                    sortBy === "isActive"
                      ? sortOrder === "asc"
                        ? "↑"
                        : "↓"
                      : null
                  }
                  sx={{ textTransform: "none", fontWeight: "bold" }}
                >
                  Status
                </Button>
              </TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subscriptions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 4 }}
                  >
                    No subscriptions found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              subscriptions.map((subscription) => (
                <TableRow key={subscription._id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: theme.palette.primary.main,
                        }}
                      >
                        {subscription.planName?.charAt(0) || "P"}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {subscription.planName || "Unknown Plan"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {subscription._id.slice(-6)}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {subscription.user?.name || subscription.userId || "Unknown User"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {subscription.user?.email || "N/A"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {formatAmount(subscription.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={subscription.interval || "N/A"}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(subscription.startDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(subscription.isActive)}</TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleStatusUpdate(subscription)}
                      color={subscription.isActive ? "error" : "success"}
                      title={
                        subscription.isActive
                          ? "Cancel Subscription"
                          : "Activate Subscription"
                      }
                    >
                      {subscription.isActive ? <DeleteIcon /> : <EditIcon />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        /* Handle view details */
                      }}
                      color="primary"
                      title="View Details"
                    >
                      <ViewIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={pagination.pages}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
            size="large"
          />
        </Box>
      )}

      {/* Status Update Dialog */}
      <Dialog
        open={statusDialogOpen}
        onClose={handleStatusDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Subscription Status</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to{" "}
            {newStatus === "active" ? "activate" : "cancel"} this subscription?
          </Typography>
          {selectedSubscription && (
            <Box
              sx={{
                mb: 2,
                p: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                borderRadius: 1,
              }}
            >
              <Typography variant="body2">
                <strong>Plan:</strong> {selectedSubscription.planName}
              </Typography>
              <Typography variant="body2">
                <strong>User:</strong> {selectedSubscription.userId}
              </Typography>
              <Typography variant="body2">
                <strong>Amount:</strong>{" "}
                {formatAmount(selectedSubscription.amount)}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason (optional)"
            value={statusReason}
            onChange={(e) => setStatusReason(e.target.value)}
            placeholder="Enter reason for status change..."
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleStatusDialogClose}
            disabled={updateStatusMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleStatusSubmit}
            variant="contained"
            color={newStatus === "active" ? "success" : "error"}
            disabled={updateStatusMutation.isLoading}
          >
            {updateStatusMutation.isLoading
              ? "Updating..."
              : `Mark as ${newStatus}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionList;
